import type {
	API_GetFinancialAnalysisPaymentsResponse,
	API_GetFinancialAnalysisResponse,
	FinancialAnalysisGranularity,
	FinancialAnalysisPaymentsResponseData,
	FinancialAnalysisResponseData,
} from "@pilatestime/shared-types";
import { queryOptions } from "@tanstack/react-query";
import api from "./api_client";

export interface FinancialAnalysisRangeParams {
	fromDate?: string;
	toDate?: string;
}

const buildRangeQuery = (range: FinancialAnalysisRangeParams): string => {
	const parts: string[] = [];
	if (range.fromDate) {
		parts.push(`fromDate=${encodeURIComponent(range.fromDate)}`);
	}
	if (range.toDate) {
		parts.push(`toDate=${encodeURIComponent(range.toDate)}`);
	}
	return parts.length > 0 ? `&${parts.join("&")}` : "";
};

export const fetchOrgMgmtFinancialAnalysis = (
	token: string,
	organizationId: string,
	granularity: FinancialAnalysisGranularity,
	range: FinancialAnalysisRangeParams,
): Promise<FinancialAnalysisResponseData> =>
	api
		.get(
			`/organizations/${organizationId}/org-management/financial-analysis?granularity=${granularity}${buildRangeQuery(range)}`,
			{
				headers: {
					Authorization: `Bearer ${token}`,
				},
			},
		)
		.then((res) => {
			const response = res.data as API_GetFinancialAnalysisResponse;
			return response.data;
		});

export const fetchOrgMgmtFinancialAnalysisQueryOptions = (
	organizationId: string,
	token: string,
	granularity: FinancialAnalysisGranularity,
	range: FinancialAnalysisRangeParams,
) =>
	queryOptions({
		queryKey: [
			"org_mgmt_financial_analysis",
			organizationId,
			granularity,
			range.fromDate ?? null,
			range.toDate ?? null,
		],
		queryFn: () =>
			fetchOrgMgmtFinancialAnalysis(token, organizationId, granularity, range),
		staleTime: 5 * 60 * 1000,
		gcTime: 10 * 60 * 1000,
		retry: 2,
		retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
	});

export interface FinancialAnalysisPaymentsParams {
	fromDate: string;
	toDate: string;
	page?: number;
	limit?: number;
}

export const fetchOrgMgmtFinancialAnalysisPayments = (
	token: string,
	organizationId: string,
	params: FinancialAnalysisPaymentsParams,
): Promise<FinancialAnalysisPaymentsResponseData> => {
	const queryParts = [
		`fromDate=${encodeURIComponent(params.fromDate)}`,
		`toDate=${encodeURIComponent(params.toDate)}`,
	];
	if (params.page !== undefined) {
		queryParts.push(`page=${params.page}`);
	}
	if (params.limit !== undefined) {
		queryParts.push(`limit=${params.limit}`);
	}

	return api
		.get(
			`/organizations/${organizationId}/org-management/financial-analysis/payments?${queryParts.join("&")}`,
			{
				headers: {
					Authorization: `Bearer ${token}`,
				},
			},
		)
		.then((res) => {
			const response = res.data as API_GetFinancialAnalysisPaymentsResponse;
			return response.data;
		});
};

export const fetchOrgMgmtFinancialAnalysisPaymentsQueryOptions = (
	organizationId: string,
	token: string,
	params: FinancialAnalysisPaymentsParams,
) =>
	queryOptions({
		queryKey: [
			"org_mgmt_financial_analysis_payments",
			organizationId,
			params.fromDate,
			params.toDate,
			params.page ?? 1,
			params.limit ?? 25,
		],
		queryFn: () =>
			fetchOrgMgmtFinancialAnalysisPayments(token, organizationId, params),
		staleTime: 5 * 60 * 1000,
		gcTime: 10 * 60 * 1000,
		retry: 2,
		retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
	});
